// src/components/UserProfileModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner, Image } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserCircle, faUser, faEnvelope, faWeight, faVenusMars,
  faBirthdayCake, faKey, faRulerVertical, faFire,
  faBullseye as faGoalTarget, faCalculator
  // faCamera // Puedes añadirlo si implementas subida de avatar
} from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';

const primaryGoalOptions = {
  maintain_weight: "Maintain Weight",
  lose_weight: "Lose Weight",
  gain_muscle: "Gain Muscle",
  improve_endurance: "Improve Endurance",
  eat_healthier: "Eat Healthier Generally",
  reduce_stress: "Reduce Stress"
};

const UserProfileModal = ({
  show,
  onClose,
  info: initialInfo,
  onProfileUpdate, // Esta es la función de MainPage que orquesta todo
  onOpenCalorieTargetsModal
}) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '', // Se muestra, no se edita aquí
    plan: '',   // Se muestra, no se edita aquí
    weight: '',
    height: '',
    age: '',
    sex: '',
    activity_level: 'sedentary',
    primary_goal: 'maintain_weight',
  });
  const [password, setPassword] = useState(''); // Estado separado para la nueva contraseña
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (show && initialInfo) {
      setFormData({
        full_name: initialInfo.full_name || '',
        email: initialInfo.email || '',
        plan: initialInfo.plan || 'Free',
        weight: initialInfo.weight || '',
        height: initialInfo.height || '',
        age: initialInfo.age || '',
        sex: initialInfo.sex || '',
        activity_level: initialInfo.activity_level || 'sedentary',
        primary_goal: initialInfo.primary_goal || 'maintain_weight',
      });
      setPassword(''); // Limpiar campo de contraseña al abrir/reabrir
      setError('');   // Limpiar errores previos
    } else if (show) {
      setFormData({
        full_name: '', email: '', plan: 'Free', weight: '', height: '', age: '', sex: '',
        activity_level: 'sedentary', primary_goal: 'maintain_weight',
      });
      setPassword('');
      setError('');
    }
  }, [show, initialInfo]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleSaveChanges = async () => {
    setError('');
    setIsLoading(true);

    // Construye el objeto con todos los datos que podrían actualizarse.
    // MainPage (a través de onProfileUpdate) decidirá cómo usar cada parte.
    const dataToSave = {
      full_name: formData.full_name,
      age: formData.age ? parseInt(formData.age, 10) : null,
      sex: formData.sex,
      height: formData.height ? parseInt(formData.height, 10) : null,
      weight: formData.weight ? parseFloat(formData.weight) : null,
      activity_level: formData.activity_level,
      primary_goal: formData.primary_goal,
      // avatar_url NO se maneja aquí para la edición, solo se muestra.
      // Si implementas subida de avatar, tendrías un flujo diferente para eso.
    };

    if (password.trim() !== '') {
      dataToSave.password = password; // Añade la contraseña si se proporcionó
    }

    try {
      // Llama a la función pasada por MainPage (que ahora es handleActualProfileUpdate).
      // Esta función en MainPage es la que llamará a tus servicios de auth y db.
      await onProfileUpdate(dataToSave);
      // Si onProfileUpdate tiene éxito (no lanza error), MainPage debería cerrar el modal.
      // Si onProfileUpdate lanza un error, se capturará abajo.
    } catch (err) {
      // Este catch es para errores lanzados por onProfileUpdate (handleActualProfileUpdate en MainPage)
      console.error("Error during onProfileUpdate, caught in UserProfileModal:", err);
      setError(err.message || "Failed to update profile. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static" keyboard={false} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faUser} className="me-2" /> {/* Cambiado a faUser para consistencia */}
          User Profile
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

        <Row className="mb-4">
          <Col md={4} className="text-center d-flex flex-column align-items-center">
            {initialInfo?.avatar_url ? (
              <Image
                src={initialInfo.avatar_url}
                roundedCircle
                style={{
                  width: '130px',
                  height: '130px',
                  objectFit: 'cover',
                  border: '4px solid #dee2e6',
                  marginBottom: '15px'
                }}
                alt={formData.full_name || initialInfo?.full_name || 'User Avatar'}
                onError={(e) => {
                  e.target.onerror = null; // Previene bucles si la imagen fallback también falla
                  // Podrías reemplazar con un placeholder SVG o nada
                  const parent = e.target.parentNode;
                  const fallbackIcon = document.createElement('div');
                  // Simular el icono de FontAwesome (o usar un componente real si es posible pasar un ref)
                  fallbackIcon.innerHTML = `<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="user-circle" class="svg-inline--fa fa-user-circle " role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" style="font-size: 130px; color: #adb5bd; margin-bottom: 15px;"><path fill="currentColor" d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM256 128c35.35 0 64 28.65 64 64s-28.65 64-64 64s-64-28.65-64-64S220.7 128 256 128zM256 448c-52.94 0-100.9-21.53-135.7-56.29C136.5 349.9 176.5 320 224 320h64c47.5 0 87.54 29.88 103.7 71.71C356.9 426.5 308.9 448 256 448z"></path></svg>`;
                  if (parent && e.target.nextSibling !== fallbackIcon) { // Evitar duplicados si onError se dispara múltiples veces
                    parent.insertBefore(fallbackIcon, e.target.nextSibling);
                  }
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <FontAwesomeIcon
                icon={faUserCircle}
                style={{
                  fontSize: '130px',
                  color: '#adb5bd',
                  marginBottom: '15px'
                }}
              />
            )}
            <h4 className="mt-2 mb-1">{formData.full_name || initialInfo?.full_name}</h4>
            <p className="text-muted small mb-0">{formData.email || initialInfo?.email}</p>
            <p className="text-muted small">Plan: {formData.plan || initialInfo?.plan}</p>
          </Col>

          <Col md={8}>
            <Form id="userProfileFormInternal"> {/* El ID no es estrictamente necesario si no usas form attribute en el botón */}
              <Form.Group className="mb-3" controlId="profileFullName">
                <Form.Label><FontAwesomeIcon icon={faUser} className="me-1 text-muted" /> Full Name</Form.Label>
                <Form.Control
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder="Enter full name"
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="profilePassword">
                <Form.Label><FontAwesomeIcon icon={faKey} className="me-1 text-muted" /> Change Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="New password (leave blank to keep current)"
                  value={password}
                  onChange={handlePasswordChange}
                  autoComplete="new-password" // Ayuda a los gestores de contraseñas
                />
                <Form.Text className="text-muted">
                  Min. 6 characters. Leave blank if you don't want to change it.
                </Form.Text>
              </Form.Group>

              {/* Resto de los campos del formulario (peso, altura, etc.) */}
              <Row className="g-3">
                <Col md={4} sm={6}>
                  <Form.Group className="mb-3" controlId="profileWeight">
                    <Form.Label><FontAwesomeIcon icon={faWeight} className="me-1 text-muted" /> Weight (kg)</Form.Label>
                    <Form.Control type="number" name="weight" value={formData.weight} onChange={handleChange} placeholder="e.g., 70" step="0.1" />
                  </Form.Group>
                </Col>
                <Col md={4} sm={6}>
                  <Form.Group className="mb-3" controlId="profileHeight">
                    <Form.Label><FontAwesomeIcon icon={faRulerVertical} className="me-1 text-muted" /> Height (cm)</Form.Label>
                    <Form.Control type="number" name="height" value={formData.height} onChange={handleChange} placeholder="e.g., 175" />
                  </Form.Group>
                </Col>
                <Col md={4} sm={12}> {/* sm=12 para que en pantallas pequeñas ocupe todo el ancho */}
                  <Form.Group className="mb-3" controlId="profileAge">
                    <Form.Label><FontAwesomeIcon icon={faBirthdayCake} className="me-1 text-muted" /> Age</Form.Label>
                    <Form.Control type="number" name="age" value={formData.age} onChange={handleChange} placeholder="e.g., 30" min="1" />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-3">
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="profileSex">
                    <Form.Label><FontAwesomeIcon icon={faVenusMars} className="me-1 text-muted" /> Sex</Form.Label>
                    <Form.Select name="sex" value={formData.sex} onChange={handleChange}>
                      <option value="">Select...</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="profileActivityLevel">
                    <Form.Label><FontAwesomeIcon icon={faFire} className="me-1 text-muted" /> Activity Level</Form.Label>
                    <Form.Select name="activity_level" value={formData.activity_level} onChange={handleChange} >
                      <option value="sedentary">Sedentary (little/no exercise)</option>
                      <option value="light">Light (exercise 1-3x/wk)</option>
                      <option value="moderate">Moderate (exercise 3-5x/wk)</option>
                      <option value="active">Active (exercise 6-7x/wk)</option>
                      <option value="very_active">Very Active (intense daily)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3" controlId="profilePrimaryGoal">
                <Form.Label><FontAwesomeIcon icon={faGoalTarget} className="me-1 text-muted" /> Primary Health Goal</Form.Label>
                <Form.Select name="primary_goal" value={formData.primary_goal} onChange={handleChange} >
                  {Object.entries(primaryGoalOptions).map(([key, displayText]) => (
                    <option key={key} value={key}>{displayText}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              {onOpenCalorieTargetsModal && (
                <Button
                  variant="outline-info"
                  className="mt-2 mb-3 w-100"
                  onClick={onOpenCalorieTargetsModal}
                >
                  <FontAwesomeIcon icon={faCalculator} className="me-2" />
                  Adjust Calorie & Macro Targets
                </Button>
              )}
            </Form>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isLoading}>Cancel</Button>
        <Button
          variant="primary"
          onClick={handleSaveChanges}
          disabled={isLoading}
          type="button" // Buena práctica
        >
          {isLoading && <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />}
          Update Profile
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

UserProfileModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  info: PropTypes.shape({
    full_name: PropTypes.string,
    email: PropTypes.string,
    plan: PropTypes.string,
    avatar_url: PropTypes.string,
    weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    age: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sex: PropTypes.string,
    activity_level: PropTypes.string,
    primary_goal: PropTypes.string,
  }),
  onProfileUpdate: PropTypes.func.isRequired,
  onOpenCalorieTargetsModal: PropTypes.func,
};

UserProfileModal.defaultProps = {
  info: {
    full_name: '', email: '', plan: 'Free', avatar_url: null, weight: '', height: '', age: '', sex: '',
    activity_level: 'sedentary', primary_goal: 'maintain_weight',
  },
  onOpenCalorieTargetsModal: null,
};

export default UserProfileModal;